import { AppLayout } from '@/components/AppLayout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useOrganization, useUpdateOrganization } from '@/hooks/useOrganization';
import { useAuth } from '@/contexts/AuthContext';
import { Skeleton } from '@/components/ui/skeleton';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Globe, Linkedin, Twitter, Info, ArrowLeft, Upload, X, ImageIcon, AlertTriangle, Trash2, Receipt, ArrowRight } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { INDUSTRIES } from '@/data/industries';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function OrganizationProfile() {
  const { t } = useTranslation();
  const { data: org, isLoading: orgLoading } = useOrganization();
  const { role } = useAuth();
  const updateOrg = useUpdateOrganization();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isAdmin = role === 'org_owner' || role === 'org_admin';

  // Company Profile
  const [companyDescription, setCompanyDescription] = useState('');
  const [companyMission, setCompanyMission] = useState('');
  const [companyValues, setCompanyValues] = useState('');
  const [companyBenefits, setCompanyBenefits] = useState('');
  const [industry, setIndustry] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [foundedYear, setFoundedYear] = useState('');
  const [headquarters, setHeadquarters] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [twitterUrl, setTwitterUrl] = useState('');
  const [origCompanyProfile, setOrigCompanyProfile] = useState<Record<string, string>>({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [lifecycleDialog, setLifecycleDialog] = useState<'deactivation' | 'deletion' | null>(null);
  const [lifecycleReason, setLifecycleReason] = useState('');

  const submitLifecycleRequest = useMutation({
    mutationFn: async ({ action, reason }: { action: 'deactivation' | 'deletion'; reason: string }) => {
      const result = action === 'deactivation'
        ? await supabase.rpc('request_forkhire_organization_deactivation', { _reason: reason.trim() || undefined })
        : await supabase.rpc('request_forkhire_organization_deletion', { _reason: reason.trim() || undefined });
      const { error } = result;
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: t('hire.organizationProfile.toasts.lifecycleSubmittedTitle'),
        description: t(lifecycleDialog === 'deletion'
          ? 'hire.organizationProfile.toasts.deletionSubmittedDescription'
          : 'hire.organizationProfile.toasts.deactivationSubmittedDescription'),
      });
      setLifecycleDialog(null);
      setLifecycleReason('');
      queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
    onError: (err: unknown) => {
      toast({ title: t('hire.organizationProfile.toasts.requestFailed'), description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    },
  });

  useEffect(() => {
    if (org) {
      const socialLinks = org.social_links && typeof org.social_links === 'object' && !Array.isArray(org.social_links)
        ? org.social_links as Record<string, unknown>
        : {};
      const linkedin = typeof socialLinks.linkedin === 'string' ? socialLinks.linkedin : '';
      const twitter = typeof socialLinks.twitter === 'string' ? socialLinks.twitter : '';
      setCompanyDescription(org.company_description || '');
      setCompanyMission(org.company_mission || '');
      setCompanyValues(org.company_values || '');
      setCompanyBenefits(org.company_benefits || '');
      setIndustry(org.industry || '');
      setCompanySize(org.company_size || '');
      setFoundedYear(org.founded_year ? String(org.founded_year) : '');
      setHeadquarters(org.headquarters || '');
      setWebsiteUrl(org.website_url || '');
      setLinkedinUrl(linkedin);
      setTwitterUrl(twitter);
      setOrigCompanyProfile({
        companyDescription: org.company_description || '',
        companyMission: org.company_mission || '',
        companyValues: org.company_values || '',
        companyBenefits: org.company_benefits || '',
        industry: org.industry || '',
        companySize: org.company_size || '',
        foundedYear: org.founded_year ? String(org.founded_year) : '',
        headquarters: org.headquarters || '',
        websiteUrl: org.website_url || '',
        linkedinUrl: linkedin,
        twitterUrl: twitter,
      });
    }
  }, [org]);

  const hasCompanyProfileChanges = companyDescription !== origCompanyProfile.companyDescription ||
    companyMission !== origCompanyProfile.companyMission ||
    companyValues !== origCompanyProfile.companyValues ||
    companyBenefits !== origCompanyProfile.companyBenefits ||
    industry !== origCompanyProfile.industry ||
    companySize !== origCompanyProfile.companySize ||
    foundedYear !== origCompanyProfile.foundedYear ||
    headquarters !== origCompanyProfile.headquarters ||
    websiteUrl !== origCompanyProfile.websiteUrl ||
    linkedinUrl !== origCompanyProfile.linkedinUrl ||
    twitterUrl !== origCompanyProfile.twitterUrl;

  const handleUploadLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !org) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: t('hire.organizationProfile.toasts.invalidFileType'), description: t('hire.organizationProfile.toasts.invalidFileTypeDescription'), variant: 'destructive' });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: t('hire.organizationProfile.toasts.fileTooLarge'), description: t('hire.organizationProfile.toasts.fileTooLargeDescription'), variant: 'destructive' });
      return;
    }
    setUploadingLogo(true);
    try {
      const ext = file.name.split('.').pop() || 'png';
      const path = `${org.id}/logo.${ext}`;
      const { error: uploadError } = await supabase.storage.from('org-logos').upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from('org-logos').getPublicUrl(path);
      const logoUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      updateOrg.mutate({ id: org.id, logo_url: logoUrl }, {
        onSuccess: () => toast({ title: t('hire.organizationProfile.toasts.logoUpdated') }),
      });
    } catch (err: unknown) {
      toast({ title: t('hire.organizationProfile.toasts.uploadFailed'), description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = () => {
    if (!org) return;
    updateOrg.mutate({ id: org.id, logo_url: null }, {
      onSuccess: () => toast({ title: t('hire.organizationProfile.toasts.logoRemoved') }),
    });
  };

  const handleSaveCompanyProfile = async () => {
    if (!org || !hasCompanyProfileChanges) return;
    setSavingProfile(true);
    updateOrg.mutate(
      {
        id: org.id,
        company_description: companyDescription || null,
        company_mission: companyMission || null,
        company_values: companyValues || null,
        company_benefits: companyBenefits || null,
        industry: industry || null,
        company_size: companySize || null,
        founded_year: foundedYear ? parseInt(foundedYear) : null,
        headquarters: headquarters || null,
        website_url: websiteUrl || null,
        social_links: { linkedin: linkedinUrl || '', twitter: twitterUrl || '' },
      },
      {
        onSuccess: () => {
          setOrigCompanyProfile({
            companyDescription, companyMission, companyValues, companyBenefits,
            industry, companySize, foundedYear, headquarters, websiteUrl, linkedinUrl, twitterUrl,
          });
          setSavingProfile(false);
        },
        onError: () => setSavingProfile(false),
        onSettled: () => setSavingProfile(false),
      }
    );
  };

  const handleSubmitLifecycleRequest = () => {
    if (!lifecycleDialog) return;
    if (!lifecycleReason.trim()) {
      toast({
        title: t('hire.organizationProfile.toasts.reasonRequired'),
        description: t('hire.organizationProfile.toasts.lifecycleReasonRequiredDescription'),
        variant: 'destructive',
      });
      return;
    }
    submitLifecycleRequest.mutate({ action: lifecycleDialog, reason: lifecycleReason });
  };

  const lifecycleStatus = org?.lifecycle_status || 'active';
  const isOrgOwner = role === 'org_owner';

  return (
    <AppLayout>
      <div className="space-y-6 max-w-3xl mx-auto">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="p-2 rounded-lg hover:bg-muted/60 transition-colors text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-display text-foreground">{t('hire.organizationProfile.title')}</h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">{t('hire.organizationProfile.subtitle')}</p>
          </div>
        </div>

        {orgLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-12 w-full rounded-xl" />
            <Skeleton className="h-12 w-full rounded-xl" />
            <Skeleton className="h-12 w-full rounded-xl" />
          </div>
        ) : (
          <div className="space-y-5">
            <Card className="p-6 sm:p-8 shadow-card border-border space-y-5 rounded-xl">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-accent" />
                    <h2 className="font-display font-semibold text-foreground text-lg">{t('hire.organizationProfile.lifecycle.title')}</h2>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('hire.organizationProfile.lifecycle.description')}
                  </p>
                </div>
                <Badge variant={lifecycleStatus === 'active' ? 'default' : 'destructive'} className="w-fit">
                  {lifecycleStatus.replace(/_/g, ' ')}
                </Badge>
              </div>

              <div className="rounded-xl border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
                {t('hire.organizationProfile.lifecycle.accountDeletionNotice')}
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  className="gap-1.5 rounded-xl"
                  disabled={!isOrgOwner || submitLifecycleRequest.isPending}
                  onClick={() => setLifecycleDialog('deactivation')}
                >
                  <AlertTriangle className="w-4 h-4" />
                  {t('hire.organizationProfile.lifecycle.requestDeactivation')}
                </Button>
                <Button
                  variant="destructive"
                  className="gap-1.5 rounded-xl"
                  disabled={!isOrgOwner || submitLifecycleRequest.isPending}
                  onClick={() => setLifecycleDialog('deletion')}
                >
                  <Trash2 className="w-4 h-4" />
                  {t('hire.organizationProfile.lifecycle.requestDeletionReview')}
                </Button>
              </div>
              {!isOrgOwner && (
                <p className="text-xs text-muted-foreground">
                  {t('hire.organizationProfile.lifecycle.ownerOnly')}
                </p>
              )}
            </Card>

            <Card className="p-6 sm:p-8 shadow-card border-border space-y-4 rounded-xl">
              <div className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-accent" />
                <h2 className="font-display font-semibold text-foreground text-lg">{t('hire.organizationProfile.unifiedIdentity.title')}</h2>
              </div>
              <p className="text-sm text-muted-foreground">{t('hire.organizationProfile.unifiedIdentity.description')}</p>
              <p className="text-xs text-muted-foreground">{t('hire.organizationProfile.unifiedIdentity.noDuplicateLegalFields')}</p>
              <Button asChild variant="outline" className="rounded-xl gap-2">
                <Link to="/settings/billing/identity">
                  {t('hire.organizationProfile.unifiedIdentity.open')}
                  <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </Link>
              </Button>
            </Card>

            {/* Company Profile */}
            {isAdmin && (
              <Card className="p-6 sm:p-8 shadow-card border-border space-y-5 rounded-xl">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="w-5 h-5 text-accent" />
                  <h2 className="font-display font-semibold text-foreground text-lg">{t('hire.organizationProfile.company.title')}</h2>
                </div>
                <p className="text-xs text-muted-foreground -mt-3">
                  {t('hire.organizationProfile.company.description')}
                </p>

                {/* Company Logo Upload */}
                <div className="flex items-center gap-5 p-4 rounded-xl border border-border bg-muted/30">
                  <Avatar className="h-20 w-20 rounded-xl border border-border">
                    <AvatarImage src={org?.logo_url || ''} alt={t('hire.organizationProfile.company.logoAlt')} className="object-cover" />
                    <AvatarFallback className="rounded-xl bg-muted text-muted-foreground">
                      <ImageIcon className="w-8 h-8" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-2">
                    <p className="text-sm font-medium text-foreground">{t('hire.organizationProfile.company.logo')}</p>
                    <p className="text-xs text-muted-foreground">{t('hire.organizationProfile.company.logoHelp')}</p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5 rounded-lg"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                      >
                        <Upload className="w-3.5 h-3.5" />
                        {uploadingLogo ? t('hire.organizationProfile.company.uploading') : org?.logo_url ? t('common.change') : t('common.upload')}
                      </Button>
                      {org?.logo_url && (
                        <Button variant="ghost" size="sm" className="gap-1.5 rounded-lg text-muted-foreground" onClick={handleRemoveLogo}>
                          <X className="w-3.5 h-3.5" /> {t('common.remove')}
                        </Button>
                      )}
                      <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={handleUploadLogo} />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5 text-muted-foreground" /> {t('hire.organizationProfile.company.about')}
                    </label>
                    <Textarea
                      value={companyDescription}
                      onChange={(e) => setCompanyDescription(e.target.value)}
                      placeholder={t('hire.organizationProfile.company.aboutPlaceholder')}
                      className="min-h-[120px] rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">{t('hire.organizationProfile.company.mission')}</label>
                    <Textarea
                      value={companyMission}
                      onChange={(e) => setCompanyMission(e.target.value)}
                      placeholder={t('hire.organizationProfile.company.missionPlaceholder')}
                      className="min-h-[80px] rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">{t('hire.organizationProfile.company.values')}</label>
                    <Textarea
                      value={companyValues}
                      onChange={(e) => setCompanyValues(e.target.value)}
                      placeholder={t('hire.organizationProfile.company.valuesPlaceholder')}
                      className="min-h-[80px] rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">{t('hire.organizationProfile.company.benefits')}</label>
                    <Textarea
                      value={companyBenefits}
                      onChange={(e) => setCompanyBenefits(e.target.value)}
                      placeholder={t('hire.organizationProfile.company.benefitsPlaceholder')}
                      className="min-h-[80px] rounded-xl"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">{t('hire.organizationProfile.company.industry')}</label>
                      <Select value={industry} onValueChange={setIndustry}>
                        <SelectTrigger className="h-11 rounded-xl">
                          <SelectValue placeholder={t('hire.organizationProfile.company.selectIndustry')} />
                        </SelectTrigger>
                        <SelectContent>
                          {INDUSTRIES.map(ind => (
                            <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">{t('hire.organizationProfile.company.size')}</label>
                      <Select value={companySize} onValueChange={setCompanySize}>
                        <SelectTrigger className="h-11 rounded-xl">
                          <SelectValue placeholder={t('hire.organizationProfile.company.selectSize')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1-10">{t('hire.organizationProfile.company.sizeOptions.1_10')}</SelectItem>
                          <SelectItem value="11-50">{t('hire.organizationProfile.company.sizeOptions.11_50')}</SelectItem>
                          <SelectItem value="51-200">{t('hire.organizationProfile.company.sizeOptions.51_200')}</SelectItem>
                          <SelectItem value="201-500">{t('hire.organizationProfile.company.sizeOptions.201_500')}</SelectItem>
                          <SelectItem value="501-1000">{t('hire.organizationProfile.company.sizeOptions.501_1000')}</SelectItem>
                          <SelectItem value="1001-5000">{t('hire.organizationProfile.company.sizeOptions.1001_5000')}</SelectItem>
                          <SelectItem value="5001+">{t('hire.organizationProfile.company.sizeOptions.5001_plus')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">{t('hire.organizationProfile.company.foundedYear')}</label>
                      <Input
                        type="number"
                        value={foundedYear}
                        onChange={(e) => setFoundedYear(e.target.value)}
                        placeholder={t('hire.organizationProfile.company.foundedYearPlaceholder')}
                        min={1800}
                        max={new Date().getFullYear()}
                        className="h-11 rounded-xl"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">{t('hire.organizationProfile.company.headquarters')}</label>
                      <Input
                        value={headquarters}
                        onChange={(e) => setHeadquarters(e.target.value)}
                        placeholder={t('hire.organizationProfile.company.headquartersPlaceholder')}
                        className="h-11 rounded-xl"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                        <Globe className="w-3.5 h-3.5 text-muted-foreground" /> {t('hire.organizationProfile.company.website')}
                      </label>
                      <Input
                        type="url"
                        value={websiteUrl}
                        onChange={(e) => setWebsiteUrl(e.target.value)}
                        placeholder="https://www.company.com"
                        className="h-11 rounded-xl"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                        <Linkedin className="w-3.5 h-3.5 text-muted-foreground" /> LinkedIn
                      </label>
                      <Input
                        type="url"
                        value={linkedinUrl}
                        onChange={(e) => setLinkedinUrl(e.target.value)}
                        placeholder="https://linkedin.com/company/..."
                        className="h-11 rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                        <Twitter className="w-3.5 h-3.5 text-muted-foreground" /> X (Twitter)
                      </label>
                      <Input
                        type="url"
                        value={twitterUrl}
                        onChange={(e) => setTwitterUrl(e.target.value)}
                        placeholder="https://x.com/..."
                        className="h-11 rounded-xl"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  className="gradient-accent text-accent-foreground hover:opacity-90 touch-target h-10 px-5 rounded-xl"
                  onClick={handleSaveCompanyProfile}
                  disabled={!hasCompanyProfileChanges || savingProfile}
                >
                  {savingProfile ? t('common.saving') : t('hire.organizationProfile.company.saveProfile')}
                </Button>
              </Card>
            )}
          </div>
        )}
      </div>
      <Dialog open={!!lifecycleDialog} onOpenChange={(open) => {
        if (!open) {
          setLifecycleDialog(null);
          setLifecycleReason('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {lifecycleDialog === 'deletion' ? t('hire.organizationProfile.lifecycle.deletionDialogTitle') : t('hire.organizationProfile.lifecycle.deactivationDialogTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t('hire.organizationProfile.lifecycle.dialogDescription')}
            </p>
            <Textarea
              value={lifecycleReason}
              onChange={(e) => setLifecycleReason(e.target.value)}
              placeholder={t('hire.organizationProfile.lifecycle.reasonPlaceholder')}
              className="min-h-[100px] rounded-xl"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLifecycleDialog(null)}>{t('common.cancel')}</Button>
            <Button
              variant={lifecycleDialog === 'deletion' ? 'destructive' : 'default'}
              onClick={handleSubmitLifecycleRequest}
              disabled={submitLifecycleRequest.isPending || !lifecycleReason.trim()}
            >
              {submitLifecycleRequest.isPending ? t('common.submitting') : t('hire.organizationProfile.lifecycle.submitRequest')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
